import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, X, UserCheck, Building2, FileText, Disc, Loader2 } from "lucide-react";
import api from "../../utils/api";

const TYPE_META = {
  artist_application: { icon: UserCheck,  color: "#3b82f6", label: "Artist-Bewerbung" },
  label_application:  { icon: Building2,  color: "#8b5cf6", label: "Label-Bewerbung"  },
  bio_approval:       { icon: FileText,   color: "#f59e0b", label: "Bio-Genehmigung"  },
  release_proposal:   { icon: Disc,       color: "#ec4899", label: "Release-Vorschlag"},
};

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "Gerade eben";
  if (diff < 3600) return `Vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `Vor ${Math.floor(diff / 3600)} Std.`;
  return `Vor ${Math.floor(diff / 86400)} Tagen`;
}

function NotifItem({ n, accentColor, onRead, onClose }) {
  const [expanded, setExpanded] = useState(false);
  const Meta = TYPE_META[n.type] || { icon: Bell, color: "#52525b", label: n.type };
  const Icon = Meta.icon;
  const isLong = (n.message?.length ?? 0) > 60;

  return (
    <div
      className="px-4 py-3 transition-all duration-150 cursor-pointer select-none"
      style={{
        background: n.read ? "transparent" : `rgba(${accentColor === "amber" ? "245,158,11" : "236,72,153"},0.03)`,
        borderBottom: "1px solid rgba(255,255,255,0.03)",
      }}
      onClick={() => { onRead(n.id); setExpanded(v => !v); }}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: `${Meta.color}18`, border: `1px solid ${Meta.color}25` }}>
          <Icon size={14} style={{ color: Meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-xs font-semibold leading-snug ${n.read ? "text-zinc-400" : "text-white"}`}>{n.title}</p>
            {!n.read && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1 ${accentColor === "amber" ? "bg-amber-400" : "bg-rose-400"}`} />}
          </div>
          <p className={`text-zinc-500 text-[11px] mt-0.5 ${expanded ? "whitespace-pre-wrap break-words" : "line-clamp-2"}`}>
            {n.message}
          </p>
          {isLong && (
            <span className={`text-[10px] mt-0.5 inline-block ${accentColor === "amber" ? "text-amber-500/60" : "text-rose-500/60"}`}>
              {expanded ? "▲ weniger" : "▼ mehr lesen"}
            </span>
          )}
          {expanded && n.link && (
            <Link
              to={n.link}
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold transition-colors ${accentColor === "amber" ? "text-amber-400 hover:text-amber-300" : "text-rose-400 hover:text-rose-300"}`}
            >
              {accentColor === "amber" ? "Zum Bereich →" : "Ansehen →"}
            </Link>
          )}
          <p className="text-zinc-700 text-[10px] mt-1">{timeAgo(n.created_at)}</p>
        </div>
      </div>
    </div>
  );
}

export function AdminNotificationBell({ token }) {
  const [open, setOpen]           = useState(false);
  const [count, setCount]         = useState(0);
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [pushStatus, setPushStatus] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const panelRef                  = useRef(null);
  const headers                   = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchCount = useCallback(async () => {
    try {
      const res = await api.get("/notifications/admin/unread-count", { headers });
      const newCount = res.data?.count ?? 0;
      if (newCount > count && count > 0 && Notification.permission === "granted") {
        new Notification("HUWND Admin", { body: "Neue Benachrichtigung eingegangen", icon: "/favicon.ico" });
      }
      setCount(newCount);
    } catch {}
  }, [count, headers]); // eslint-disable-line

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications/admin/list", { headers });
      setItems(res.data || []);
    } catch {}
    setLoading(false);
  }, [headers]); // eslint-disable-line

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 30_000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  const requestPush = async () => {
    const result = await Notification.requestPermission();
    setPushStatus(result);
  };

  const handleOpen = () => {
    setOpen(v => !v);
    if (!open) fetchItems();
  };

  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async (id) => {
    await api.post("/notifications/admin/mark-read", { id }, { headers });
    setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.post("/notifications/admin/mark-read", {}, { headers });
    setItems(prev => prev.map(n => ({ ...n, read: true })));
    setCount(0);
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Glocke */}
      <button
        onClick={handleOpen}
        data-testid="admin-notification-bell"
        className="relative flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200 hover:bg-white/[0.07]"
        style={{ color: count > 0 ? "#f59e0b" : "#52525b" }}
        title="Benachrichtigungen"
      >
        <Bell size={18} />
        {count > 0 && (
          <span
            data-testid="admin-notif-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-black text-white px-1"
            style={{ background: "linear-gradient(135deg,#f59e0b,#ef4444)", boxShadow: "0 2px 8px rgba(239,68,68,0.5)" }}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="absolute right-0 top-11 w-[min(320px,_90vw)] rounded-2xl overflow-hidden z-50 lg:right-auto lg:left-0"
          style={{
            background: "linear-gradient(180deg, #141414 0%, #0f0f0f 100%)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04) inset",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center gap-2">
              <Bell size={13} className="text-amber-400" />
              <span className="text-white text-sm font-bold">Benachrichtigungen</span>
              {count > 0 && (
                <span className="text-[10px] font-black text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded-full">{count} neu</span>
              )}
            </div>
            {items.some(n => !n.read) && (
              <button onClick={markAllRead} className="text-zinc-500 hover:text-zinc-300 text-xs flex items-center gap-1 transition-colors">
                <Check size={11} /> Alle gelesen
              </button>
            )}
          </div>

          {/* Push-Permission Banner */}
          {pushStatus === "default" && (
            <div className="px-4 py-3 flex items-center gap-3" style={{ background: "rgba(245,158,11,0.06)", borderBottom: "1px solid rgba(245,158,11,0.12)" }}>
              <Bell size={13} className="text-amber-400 flex-shrink-0" />
              <p className="text-amber-300/80 text-xs flex-1">Browser-Push aktivieren um auch bei neuem Tab benachrichtigt zu werden.</p>
              <button
                onClick={requestPush}
                className="text-[11px] font-bold text-amber-400 hover:text-amber-300 bg-amber-400/10 hover:bg-amber-400/20 px-2.5 py-1 rounded-lg transition-all flex-shrink-0"
              >
                Erlauben
              </button>
            </div>
          )}
          {pushStatus === "denied" && (
            <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: "rgba(239,68,68,0.06)", borderBottom: "1px solid rgba(239,68,68,0.1)" }}>
              <X size={12} className="text-red-500 flex-shrink-0" />
              <p className="text-red-400/70 text-[11px]">Push blockiert – in Browser-Einstellungen freigeben.</p>
            </div>
          )}

          {/* Liste */}
          <div className="max-h-80 overflow-y-auto" style={{ scrollbarWidth: "none" }}>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 size={16} className="animate-spin text-zinc-700" /></div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center">
                <Bell size={24} className="mx-auto mb-2 text-zinc-800" />
                <p className="text-zinc-700 text-xs">Keine Benachrichtigungen</p>
              </div>
            ) : items.map(n => (
              <NotifItem key={n.id} n={n} accentColor="amber" onRead={markRead} onClose={() => setOpen(false)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
